import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, rupee } from "../lib/api";
import { useStores } from "../lib/stores";
import { formatSaleDate } from "../lib/saleInvoice";
import { ChevronRight, PackagePlus, Truck } from "lucide-react";

const GRID_HEAD = "purchases-grid-head";
const GRID_ROW = "purchases-grid-row";

function statusLabel(status) {
  if (status === "full_return") return "Fully returned";
  if (status === "partial_return") return "Partial return";
  return "Active";
}

function statusStyle(status) {
  if (status === "full_return") {
    return { background: "var(--surface-muted)", color: "var(--text-muted)", dot: "var(--text-muted)" };
  }
  if (status === "partial_return") {
    return { background: "#fef3c7", color: "#92400e", dot: "#d97706" };
  }
  return { background: "var(--surface-success)", color: "var(--success)", dot: "var(--success)" };
}

function StatusBadge({ status }) {
  const style = statusStyle(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap"
      style={{ background: style.background, color: style.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: style.dot }} />
      {statusLabel(status)}
    </span>
  );
}

export default function Purchases() {
  const [items, setItems] = useState([]);
  const { activeStoreId } = useStores();
  const navigate = useNavigate();

  useEffect(() => {
    const params = activeStoreId ? { store_id: activeStoreId } : {};
    api.get("/purchases", { params }).then((r) => setItems(r.data));
  }, [activeStoreId]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="purchases-root">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Stock purchases</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Record stock received from suppliers · partial or full returns supported
          </p>
        </div>
        <Link
          to="/app/purchases/new"
          className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-white tactile-btn font-semibold"
          style={{ background: "var(--primary)" }}
          data-testid="purchases-new-btn"
        >
          <PackagePlus size={18} /> New purchase
        </Link>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-0">
            <div className={GRID_HEAD} aria-hidden>
              <div>Purchase #</div>
              <div>Date</div>
              <div>Supplier</div>
              <div>Status</div>
              <div className="purchases-col-center">Items</div>
              <div className="purchases-col-num">Total</div>
              <div className="purchases-col-num">Returned</div>
            </div>

            {items.length === 0 && (
              <div className="py-16 text-center">
                <Truck size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No purchases yet</p>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                  Record stock received from your supplier to track returns.
                </p>
              </div>
            )}

            {items.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                className={`${GRID_ROW} group`}
                onClick={() => navigate(`/app/purchases/${p.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/app/purchases/${p.id}`);
                  }
                }}
                data-testid={`purchase-row-${p.purchase_no}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-sm font-semibold truncate" style={{ color: "var(--primary)" }}>
                    {p.purchase_no}
                  </span>
                  <ChevronRight
                    size={15}
                    className="shrink-0 opacity-0 -translate-x-1 group-hover:opacity-50 group-hover:translate-x-0 transition-all"
                    style={{ color: "var(--text-muted)" }}
                  />
                </div>
                <div className="text-sm whitespace-nowrap">{formatSaleDate(p.created_at)}</div>
                <div className="font-medium truncate">{p.supplier_snapshot?.name || "Supplier"}</div>
                <div><StatusBadge status={p.status} /></div>
                <div className="purchases-col-center text-sm num">{p.items.length}</div>
                <div className="purchases-col-num text-sm font-semibold num">{rupee(p.total)}</div>
                <div className="purchases-col-num text-sm num" style={{ color: p.returned_total > 0 ? "var(--error)" : "var(--text-muted)" }}>
                  {p.returned_total > 0 ? rupee(p.returned_total) : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
